/**
 * @module NotificationPanel
 * @package @geenius/notifications-react
 * @description Renders the compact notification drawer used by the Tailwind
 * React variant. The panel exposes keyboard-accessible controls for closing the
 * drawer and marking items as read.
 */

import { motion } from '@geenius/motion/react'
import type { Notification } from '@geenius/notifications/shared'
import type { JSX, KeyboardEvent as ReactKeyboardEvent } from 'react'
import { useEffect, useRef } from 'react'
import { useNotificationCopy } from '../lib/notificationCopy'
import { cn } from '../lib/utils'
import { EmptyNotifications } from './EmptyNotifications'
import { NotificationItem } from './NotificationItem'

const FOCUSABLE_SELECTORS = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

function getFocusableElements(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS))
    .filter((element) => element.getAttribute('aria-hidden') !== 'true')
}

/**
 * Props for the React notification panel.
 */
export interface NotificationPanelProps {
  notifications: Notification[]
  onMarkAll?: () => void
  onClose?: () => void
  onRead?: (id: string) => void
  onDismiss?: (id: string) => void
  onViewAll?: () => void
  panelId?: string
  title?: string
  className?: string
}

/**
 * Renders the notification drawer panel.
 *
 * @param props Drawer props including notification data and action handlers.
 * @returns The drawer panel markup.
 */
export function NotificationPanel({
  notifications,
  onMarkAll,
  onClose,
  onRead,
  onDismiss,
  onViewAll,
  panelId = 'notifications-panel',
  title,
  className,
}: NotificationPanelProps): JSX.Element {
  const { t } = useNotificationCopy()
  const panelRef = useRef<HTMLElement | null>(null)
  const unreadCount = notifications.filter(
    (notification) => notification.status === 'unread',
  ).length
  const titleId = `${panelId}-title`

  useEffect(() => {
    const panel = panelRef.current

    if (!panel) {
      return
    }

    const previousFocus =
      document.activeElement instanceof HTMLElement ? document.activeElement : null
    const focusable = getFocusableElements(panel)

    ;(focusable[0] ?? panel).focus()

    return () => {
      previousFocus?.focus()
    }
  }, [])

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLElement>): void => {
    if (event.key === 'Escape') {
      event.preventDefault()
      onClose?.()
      return
    }

    if (event.key !== 'Tab') {
      return
    }

    const panel = panelRef.current

    if (!panel) {
      return
    }

    const focusable = getFocusableElements(panel)

    if (focusable.length === 0) {
      event.preventDefault()
      panel.focus()
      return
    }

    const firstFocusable = focusable[0]
    const lastFocusable = focusable[focusable.length - 1]
    const activeElement = document.activeElement

    if (event.shiftKey) {
      if (activeElement === firstFocusable || activeElement === panel) {
        event.preventDefault()
        lastFocusable.focus()
      }
      return
    }

    if (activeElement === lastFocusable) {
      event.preventDefault()
      firstFocusable.focus()
    }
  }

  return (
    <motion.section
      ref={panelRef}
      id={panelId}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      tabIndex={-1}
      onKeyDown={handleKeyDown}
      initial={{ opacity: 0, scale: 0.96, y: 18 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.24 }}
      className={cn(
        'flex max-h-[28rem] w-96 flex-col overflow-hidden rounded-2xl border border-[var(--gn-notifications-panel-border)] bg-[var(--gn-notifications-panel-surface)] shadow-2xl backdrop-blur-xl',
        className,
      )}
    >
      <div className="flex items-center justify-between border-b border-[var(--gn-notifications-panel-border)] bg-[var(--gn-notifications-panel-surface-muted)] px-5 py-4">
        <span
          id={titleId}
          className="flex items-center gap-2 text-sm font-bold tracking-wide text-foreground"
        >
          {title ?? t('notifications.common.title')}
          <span className="rounded-full bg-primary/20 px-2 py-0.5 text-[10px] font-bold text-primary">
            {unreadCount}
          </span>
        </span>
        <div className="flex items-center gap-3">
          <motion.button
            type="button"
            onClick={onMarkAll}
            disabled={!onMarkAll || unreadCount === 0}
            whileHover={{ y: -1, scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground transition-colors hover:text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t('notifications.common.markAllRead')}
          </motion.button>
          <motion.button
            type="button"
            onClick={onClose}
            aria-label={t('notifications.panel.closeAria')}
            whileHover={{ scale: 1.08, rotate: 90 }}
            whileTap={{ scale: 0.95 }}
            className="flex h-7 w-7 items-center justify-center rounded-full bg-[var(--gn-notifications-panel-surface-muted)] p-1.5 text-muted-foreground transition-colors hover:bg-destructive/20 hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          >
            ✕
          </motion.button>
        </div>
      </div>
      <div className="custom-scrollbar flex-1 overflow-y-auto overflow-x-hidden bg-[var(--gn-notifications-panel-backdrop)]">
        {notifications.length === 0 ? (
          <EmptyNotifications />
        ) : (
          <ul>
            {notifications.map((notification) => (
              <li
                key={notification.id}
                className="border-b border-[var(--gn-notifications-panel-border)] last:border-b-0"
              >
                <NotificationItem
                  notification={notification}
                  onRead={onRead}
                  onDismiss={onDismiss}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
      {notifications.length > 0 && onViewAll ? (
        <div className="border-t border-[var(--gn-notifications-panel-border)] bg-[var(--gn-notifications-panel-surface-muted)] px-5 py-3 text-center">
          <motion.button
            type="button"
            onClick={onViewAll}
            whileHover={{ y: -1, scale: 1.02 }}
            whileTap={{ scale: 0.97 }}
            className="text-xs font-bold uppercase tracking-widest text-primary transition-colors hover:text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          >
            {t('notifications.common.viewAll')}
          </motion.button>
        </div>
      ) : null}
    </motion.section>
  )
}
